import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';

declare module 'axios' {
  export interface AxiosRequestConfig {
    skipErrorToast?: boolean;
  }
}

/**
 * API base URL resolution.
 * - If VITE_API_BASE_URL is provided, use it.
 * - Otherwise use same-origin '/api' so Vite proxy can route to backend in local dev.
 */
function resolveApiBaseUrl(): string {
  const env = import.meta.env.VITE_API_BASE_URL;
  if (typeof env === 'string' && env.trim().length > 0) {
    return env.trim();
  }
  return '/api';
}

const API_BASE_URL = resolveApiBaseUrl();

/**
 * Create axios instance with default configuration
 */
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor - Add auth token to requests
 */
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token =
      localStorage.getItem('accessToken') || localStorage.getItem('token');
    const url = config.url || '';
    const isPublicAuth =
      url.includes('/login') ||
      url.includes('/auth/login') ||
      url.includes('/auth/register') ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/forgot-password') ||
      url.includes('/auth/reset-password');

    if (token && config.headers && !isPublicAuth) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor - Handle errors and token refresh
 */
api.interceptors.response.use(
  (response) => {
    console.log('API Response Interceptor - Success:', response.config.url, response.status);
    return response;
  },
  async (error: AxiosError<any>) => {
    console.error('API Response Interceptor - Error:', error.config?.url, error.response?.status, error.message);
    console.error('API Response Interceptor - Error details:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message
    });
    
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 errors - don't immediately logout, let components handle it
    // This prevents immediate logout after login if there's a token validation issue
    if (error.response?.status === 401 && !originalRequest.url?.includes('/login')) {
      // Don't try to refresh since refresh endpoint doesn't exist
      // Just reject the error and let the calling component handle it
      // This prevents the interceptor from immediately logging out the user
      return Promise.reject(error);
    }

    // Handle other errors
    const errorMessage = error.response?.data?.message || 'An error occurred';
    
    // Show toast notification for errors (but NOT for login endpoint - let LoginPage handle it)
    const skipToast = originalRequest.skipErrorToast;
    if (error.response?.status !== 401 && !originalRequest.url?.includes('/login') && !skipToast) {
      toast.error(errorMessage);
    }

    return Promise.reject(error);
  }
);

export default api;


